import { useCallback, useMemo } from "react";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useCompanyList } from "@/api/services/main/company";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH } from "@/features/company/components/company-list/company-filter-shared";

export const useCompanyFilterMasterData = () => {
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();

  // Fetch all companies for dropdown options (management numbers, company names, contact persons)
  const { data: allCompanies } = useCompanyList({ isAll: "1" });

  const managementNumberOptions = useMemo<TOption[]>(() => {
    if (!allCompanies?.data) return [];
    const unique = new Set<string>();
    return allCompanies.data
      .filter((c) => {
        if (!c.managementNumber || unique.has(c.managementNumber)) return false;
        unique.add(c.managementNumber);
        return true;
      })
      .map((c) => ({ label: c.managementNumber!, value: c.managementNumber! }));
  }, [allCompanies]);

  const companyNameOptions = useMemo<TOption[]>(() => {
    if (!allCompanies?.data) return [];
    const unique = new Set<string>();
    return allCompanies.data
      .filter((c) => {
        if (!c.companyName || unique.has(c.companyName)) return false;
        unique.add(c.companyName);
        return true;
      })
      .map((c) => ({ label: c.companyName, value: c.companyName }));
  }, [allCompanies]);

  const contactPersonOptions = useMemo<TOption[]>(() => {
    if (!allCompanies?.data) return [];
    const unique = new Set<string>();
    return allCompanies.data
      .filter((c) => {
        if (!c.contactPersonName?.trim() || unique.has(c.contactPersonName)) return false;
        unique.add(c.contactPersonName);
        return true;
      })
      .map((c) => ({ label: c.contactPersonName!, value: c.contactPersonName! }));
  }, [allCompanies]);

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) =>
      options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );

  // Map prefecture options to use label as value (backend expects prefecture name, not code)
  const prefectureNameOptions = useMemo<TOption[]>(
    () => provinceOptions.map((opt) => ({ label: opt.label, value: opt.label })),
    [provinceOptions],
  );

  const truncatedOptions = useMemo(
    () => ({
      managementNumber: truncateOpts(managementNumberOptions),
      companyName: truncateOpts(companyNameOptions),
      industry: truncateOpts(companySpecificIndustryOptions),
      prefecture: truncateOpts(prefectureNameOptions),
      contactPerson: truncateOpts(contactPersonOptions),
    }),
    [truncateOpts, managementNumberOptions, companyNameOptions, companySpecificIndustryOptions, prefectureNameOptions, contactPersonOptions],
  );

  // Get municipality options (using name as value) for multiple selected prefectures
  const getMunicipalityNameOptionsByPrefectures = useCallback(
    (prefectureNames: string[]): TOption[] => {
      const allOptions: TOption[] = [];
      for (const name of prefectureNames) {
        const province = provinceOptions.find((opt) => opt.label === name);
        if (!province) continue;
        const municipalityOptions = getMunicipalityOptionsByProvince(String(province.value));
        allOptions.push(...municipalityOptions.map((opt) => ({ label: opt.label, value: opt.label })));
      }
      return allOptions;
    },
    [provinceOptions, getMunicipalityOptionsByProvince],
  );

  const rawOptions = useMemo(
    () => ({
      companyName: companyNameOptions,
      industry: companySpecificIndustryOptions,
      prefecture: prefectureNameOptions,
      contactPerson: contactPersonOptions,
    }),
    [companyNameOptions, companySpecificIndustryOptions, prefectureNameOptions, contactPersonOptions],
  );

  return { truncatedOptions, rawOptions, getMunicipalityNameOptionsByPrefectures };
};
